import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  FormHelperText,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { parentService, roleService } from '../../services/apiService';

const ParentForm = ({ open, onClose, onSubmit, initialData, title = "Add Parent", externalErrors = {} }) => {
  const [formData, setFormData] = useState({
    role_id: '',
    full_name: '',
    email: '',
    phone: '',
    password_hash: '',
    date_of_joining: '',
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState(null);

  // Fetch roles when modal opens
  const fetchRoles = useCallback(async () => {
    if (!open) return;
    
    setRolesLoading(true);
    setRolesError(null);
    try {
      const response = await roleService.getAll();
      setRoles(response.data || response);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRolesError('Failed to fetch roles');
    } finally {
      setRolesLoading(false);
    }
  }, [open]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Auto-select Parent role for new parents
  useEffect(() => {
    if (roles.length > 0 && !initialData?.id) {
      const parentRole = roles.find(role => role.role_name?.toLowerCase() === 'parent');
      if (parentRole) {
        setFormData(prev => ({
          ...prev,
          role_id: parentRole.id
        }));
      }
    }
  }, [roles, initialData?.id]);

  // Initialize form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        role_id: initialData.role_id || '',
        full_name: initialData.full_name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        password_hash: '', // Don't pre-fill password for security
        date_of_joining: initialData.date_of_joining || '',
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
      });
    } else {
      // Reset form for new parent
      setFormData({
        role_id: '',
        full_name: '',
        email: '',
        phone: '',
        password_hash: '',
        date_of_joining: '',
        is_active: true,
      });
    }
    setErrors({});
  }, [initialData]);
  
  // Apply external errors (from API) to form
  useEffect(() => {
    if (Object.keys(externalErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...externalErrors }));
    }
  }, [externalErrors]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      // Validate phone: must start with 9 or 6, exactly 10 digits
      const phoneRegex = /^[9876]\d{9}$/;
      if (!phoneRegex.test(formData.phone.trim())) {
        newErrors.phone = 'Phone number must start with 9,8,7 or 6 and be exactly 10 digits';
      }
    }

    if (!formData.date_of_joining) {
      newErrors.date_of_joining = 'Date of joining is required';
    }

    // Password validation for new parents
    if (!initialData?.id && !formData.password_hash.trim()) {
      newErrors.password_hash = 'Password is required for new parents';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, initialData?.id]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const submitData = { ...formData };
      
      // Include ID for updates
      if (initialData?.id) {
        submitData.id = initialData.id;
      }

      // Remove empty password for updates
      if (initialData?.id && !submitData.password_hash) {
        delete submitData.password_hash;
      }

      await onSubmit(submitData);
      // Clear errors on successful submission
      setErrors({});
    } catch (error) {
      console.error('Form submission error:', error);
      // Error handling is done in the parent component
    }
  }, [formData, initialData?.id, validateForm, onSubmit]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {title}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box component="form" sx={{ mt: 1 }}>
          {/* Role Selection */}
          <FormControl fullWidth margin="normal" error={!!errors.role_id}>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role_id}
              onChange={handleChange('role_id')}
              label="Role"
              disabled={!initialData?.id} // Disable for new parents
            >
              {rolesLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading roles...
                </MenuItem>
              ) : rolesError ? (
                <MenuItem disabled>
                  <Alert severity="error" sx={{ width: '100%' }}>
                    {rolesError}
                  </Alert>
                </MenuItem>
              ) : (
                roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.role_name}
                  </MenuItem>
                ))
              )}
            </Select>
            {!initialData?.id && (
              <FormHelperText>
                Role is automatically set to 'Parent' for new parent accounts
              </FormHelperText>
            )}
            {errors.role_id && <FormHelperText>{errors.role_id}</FormHelperText>}
          </FormControl>

          {/* Full Name */}
          <TextField
            fullWidth
            margin="normal"
            label="Full Name"
            value={formData.full_name}
            onChange={handleChange('full_name')}
            error={!!errors.full_name}
            helperText={errors.full_name}
            required
          />

          {/* Email */}
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            required
          />

          {/* Phone */}
          <TextField
            fullWidth
            margin="normal"
            label="Phone"
            value={formData.phone}
            onChange={(e) => {
              // Only allow digits, max 10 digits
              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
              setFormData(prev => ({ ...prev, phone: value }));
              // Clear error when user starts typing
              if (errors.phone) {
                setErrors(prev => ({ ...prev, phone: '' }));
              }
            }}
            error={!!errors.phone}
            helperText={errors.phone || 'Must start with 9 or 6, exactly 10 digits'}
            required
            inputProps={{
              maxLength: 10,
              pattern: '[96]\\d{9}'
            }}
          />

          {/* Password */}
          <TextField
            fullWidth
            margin="normal"
            label="Password"
            type="password"
            value={formData.password_hash}
            onChange={handleChange('password_hash')}
            error={!!errors.password_hash}
            helperText={errors.password_hash || (initialData?.id ? 'Leave empty to keep current password' : 'Required for new parents')}
            required={!initialData?.id}
          />

          {/* Date of Joining */}
          <TextField
            fullWidth
            margin="normal"
            label="Date of Joining"
            type="date"
            value={formData.date_of_joining}
            onChange={handleChange('date_of_joining')}
            error={!!errors.date_of_joining}
            helperText={errors.date_of_joining}
            InputLabelProps={{
              shrink: true,
            }}
            required
          />

          {/* Active Status */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.is_active ? 1 : 0}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 1 }))}
              label="Status"
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
        >
          {initialData?.id ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParentForm;
